// Parser/evaluator per espressioni matematiche del libro Burgo.
// Sintassi supportata:
//   - numeri: 0.5, 1.7, 2, 5
//   - identificatori (codici misura, formule, riferimenti a punti):
//       C.T., L.V.D., ST., Tg., Tg, size, taglia, Vest., Vest, A.x, B1.y, ...
//   - operatori binari: + - * /
//   - operatore unario: -
//   - parentesi: ( )

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'ident'; name: string }
  | { kind: 'op'; op: '+' | '-' | '*' | '/' }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'comma' }

function tokenize(src: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < src.length) {
    const c = src[i]
    if (c === ' ' || c === '\t' || c === '\n') {
      i++
      continue
    }
    if (c >= '0' && c <= '9') {
      let j = i
      while (j < src.length && src[j] >= '0' && src[j] <= '9') j++
      if (src[j] === '.' && src[j + 1] >= '0' && src[j + 1] <= '9') {
        j++
        while (j < src.length && src[j] >= '0' && src[j] <= '9') j++
      }
      tokens.push({ kind: 'number', value: parseFloat(src.slice(i, j)) })
      i = j
      continue
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1
      while (j < src.length && /[A-Za-z0-9._]/.test(src[j])) j++
      tokens.push({ kind: 'ident', name: src.slice(i, j) })
      i = j
      continue
    }
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      tokens.push({ kind: 'op', op: c })
      i++
      continue
    }
    if (c === '(') {
      tokens.push({ kind: 'lparen' })
      i++
      continue
    }
    if (c === ')') {
      tokens.push({ kind: 'rparen' })
      i++
      continue
    }
    if (c === ',') {
      tokens.push({ kind: 'comma' })
      i++
      continue
    }
    throw new Error(`Carattere inatteso "${c}" alla posizione ${i}`)
  }
  return tokens
}

type Expr =
  | { kind: 'num'; value: number }
  | { kind: 'ident'; name: string }
  | { kind: 'binop'; op: '+' | '-' | '*' | '/'; left: Expr; right: Expr }
  | { kind: 'neg'; expr: Expr }
  | { kind: 'call'; name: string; args: Expr[] }

class Parser {
  private pos = 0
  constructor(private tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos]
  }

  private next(): Token | undefined {
    return this.tokens[this.pos++]
  }

  parse(): Expr {
    const expr = this.expr()
    if (this.pos < this.tokens.length) {
      throw new Error(`Token in eccesso a posizione ${this.pos}`)
    }
    return expr
  }

  private expr(): Expr {
    let left = this.term()
    while (true) {
      const t = this.peek()
      if (t?.kind === 'op' && (t.op === '+' || t.op === '-')) {
        this.next()
        const right = this.term()
        left = { kind: 'binop', op: t.op, left, right }
      } else break
    }
    return left
  }

  private term(): Expr {
    let left = this.factor()
    while (true) {
      const t = this.peek()
      if (t?.kind === 'op' && (t.op === '*' || t.op === '/')) {
        this.next()
        const right = this.factor()
        left = { kind: 'binop', op: t.op, left, right }
      } else break
    }
    return left
  }

  private factor(): Expr {
    const t = this.peek()
    if (!t) throw new Error('Espressione incompleta')
    if (t.kind === 'op' && t.op === '-') {
      this.next()
      return { kind: 'neg', expr: this.factor() }
    }
    if (t.kind === 'op' && t.op === '+') {
      this.next()
      return this.factor()
    }
    if (t.kind === 'number') {
      this.next()
      return { kind: 'num', value: t.value }
    }
    if (t.kind === 'ident') {
      this.next()
      // Chiamata di funzione: ident '(' args ')'
      if (this.peek()?.kind === 'lparen') {
        this.next()
        const args: Expr[] = []
        if (this.peek()?.kind !== 'rparen') {
          args.push(this.expr())
          while (this.peek()?.kind === 'comma') {
            this.next()
            args.push(this.expr())
          }
        }
        const close = this.next()
        if (close?.kind !== 'rparen') throw new Error('Parentesi funzione non chiusa')
        return { kind: 'call', name: t.name, args }
      }
      return { kind: 'ident', name: t.name }
    }
    if (t.kind === 'lparen') {
      this.next()
      const e = this.expr()
      const close = this.next()
      if (close?.kind !== 'rparen') throw new Error('Parentesi non chiusa')
      return e
    }
    throw new Error(`Token inatteso: ${JSON.stringify(t)}`)
  }
}

export interface EvalContext {
  // Misure del corpo: codice → valore in cm. Es. { 'C.T.': 88, 'ST.': 168 }
  measurements: Record<string, number>
  // Punti già calcolati: nome → coordinate
  points: Record<string, { x: number; y: number }>
  // Formule nominate del capo (per riusare formule con nome)
  namedFormulas: Record<string, string>
  // Valori derivati: 'tg', 'size', 'taglia', 'vest', ecc.
  derived: Record<string, number>
}

function normalizeKey(name: string): string {
  return name.replace(/\.$/, '').toLowerCase()
}

function resolveIdent(name: string, ctx: EvalContext, stack: string[]): number {
  // Riferimento a punto: termina con .x o .y (case-sensitive a runtime)
  const lower = name.toLowerCase()
  if (lower.endsWith('.x') || lower.endsWith('.y')) {
    const lastDot = name.lastIndexOf('.')
    const ptName = name.slice(0, lastDot)
    const axis = name.slice(lastDot + 1).toLowerCase()
    const pt = ctx.points[ptName]
    if (!pt) throw new Error(`Punto "${ptName}" non disponibile`)
    return axis === 'x' ? pt.x : pt.y
  }

  // Misura del corpo (prova varianti con/senza punto finale)
  const candidates = [
    name,
    name.replace(/\.$/, ''),
    name + '.',
    name.toUpperCase(),
    name.toUpperCase() + '.',
    name.toUpperCase().replace(/\.$/, ''),
  ]
  for (const k of candidates) {
    if (k in ctx.measurements) return ctx.measurements[k]
  }

  // Valore derivato (Tg, size, taglia, Vest)
  const norm = normalizeKey(name)
  if (norm in ctx.derived) return ctx.derived[norm]

  // Formula nominata (ricorsiva, con guard contro cicli)
  if (name in ctx.namedFormulas) {
    if (stack.includes(name)) {
      throw new Error(`Ciclo nelle formule: ${[...stack, name].join(' → ')}`)
    }
    return evaluateInternal(ctx.namedFormulas[name], ctx, [...stack, name])
  }

  throw new Error(`Identificatore "${name}" non risolvibile`)
}

function evaluateAst(expr: Expr, ctx: EvalContext, stack: string[]): number {
  switch (expr.kind) {
    case 'num':
      return expr.value
    case 'neg':
      return -evaluateAst(expr.expr, ctx, stack)
    case 'binop': {
      const l = evaluateAst(expr.left, ctx, stack)
      const r = evaluateAst(expr.right, ctx, stack)
      switch (expr.op) {
        case '+':
          return l + r
        case '-':
          return l - r
        case '*':
          return l * r
        case '/':
          if (r === 0) throw new Error('Divisione per zero')
          return l / r
      }
    }
    case 'ident':
      return resolveIdent(expr.name, ctx, stack)
    case 'call':
      return evaluateCall(expr, ctx, stack)
  }
}

// Distanza tra due punti riferiti per label (es. dist(G, L1))
function pointArg(e: Expr, ctx: EvalContext): { x: number; y: number } {
  if (e.kind !== 'ident') {
    throw new Error('dist() richiede label di punti, es. dist(G, L1)')
  }
  const p = ctx.points[e.name]
  if (!p) throw new Error(`Punto "${e.name}" non disponibile`)
  return p
}

function evaluateCall(
  expr: Extract<Expr, { kind: 'call' }>,
  ctx: EvalContext,
  stack: string[],
): number {
  const name = expr.name.toLowerCase()
  const nums = () => expr.args.map(a => evaluateAst(a, ctx, stack))
  switch (name) {
    case 'sqrt': {
      const [x] = nums()
      if (x < 0) throw new Error('sqrt di numero negativo')
      return Math.sqrt(x)
    }
    case 'abs':
      return Math.abs(nums()[0])
    case 'min':
      return Math.min(...nums())
    case 'max':
      return Math.max(...nums())
    case 'hypot':
      return Math.hypot(...nums())
    case 'pow': {
      const [b, e] = nums()
      return Math.pow(b, e)
    }
    case 'dist': {
      if (expr.args.length !== 2) throw new Error('dist(P, Q) richiede 2 punti')
      const a = pointArg(expr.args[0], ctx)
      const b = pointArg(expr.args[1], ctx)
      return Math.hypot(a.x - b.x, a.y - b.y)
    }
    default:
      throw new Error(`Funzione "${expr.name}" sconosciuta`)
  }
}

function evaluateInternal(formula: string, ctx: EvalContext, stack: string[]): number {
  const tokens = tokenize(formula)
  const expr = new Parser(tokens).parse()
  return evaluateAst(expr, ctx, stack)
}

export function evaluateFormula(formula: string, ctx: EvalContext): number {
  return evaluateInternal(formula, ctx, [])
}

// Try-catch wrapper: ritorna un risultato + errore se presente
export interface EvalResult {
  value: number | null
  error: string | null
}

export function tryEvaluate(formula: string, ctx: EvalContext): EvalResult {
  try {
    return { value: evaluateFormula(formula, ctx), error: null }
  } catch (e) {
    return { value: null, error: e instanceof Error ? e.message : String(e) }
  }
}

// Helper per debug/testing
export function _internal_tokenize(src: string): Token[] {
  return tokenize(src)
}
